import moment from 'moment';
import { styledDefaultColDef, createAvatarNameRenderer } from '../../../extras/cellRenderers';

import StatusDropdown from './StatusDropdown';
import EvaluatorDropdown from './EvaluatorDropdown';
import SubmittedSolutionModal from './SubmittedSolutionDetails';
import ScoreDetailsModal from './ScoreDetails';
import OnHoldPopup from './OnHoldPopup';

import { ActionCellRenderer } from './ActionCellRenderer';

const NameGetter = createAvatarNameRenderer(
  (p) => p?.data?.Students?.firstName,
  (p) => p?.data?.Students?.lastName
);

const getFullName = (params) => {
  return params?.data?.Students?.firstName
    ? params?.data?.Students?.firstName + ' ' + params?.data?.Students?.lastName
    : '';
};
const getAcceptedByName = (params) => {
  return params?.data?.acceptedBy?.firstName
    ? params?.data?.acceptedBy?.firstName +
        ' ' +
        params?.data?.acceptedBy?.lastName
    : '';
};

export const columnDefs = (allProjectList, projectCategoryList) => {
  return [
    {
      headerName: 'Name',
      field: 'name',
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        suppressAndOrCondition: true,
      },
      width: 200,
      cellRenderer: NameGetter,
      valueGetter: (params) => {
        return getFullName(params);
      },
    },

    {
      headerName: 'Email',
      field: 'email',
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        suppressAndOrCondition: true,
      },
      width: 250,
      valueGetter: (params) => {
        return params?.data?.Students?.email;
      },
      cellRenderer: (params) => {
        return (
          <div className='flex items-center h-full'>
            {params?.data?.Students?.email}
          </div>
        );
      },
    },

    {
      headerName: 'Submission Date',
      field: 'submittedAt',
      sortable: true,

      filter: 'agDateColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'greaterThan', 'inRange'],
      },

      valueGetter: (params) => {
        return moment(params?.data?.submittedAt).format('DD MMM YYYY');
      },
      cellRenderer: function SubmissionDateGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {moment(params?.data?.submittedAt).format('DD MMM YYYY')}
          </div>
        );
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
        valueA - valueB,
    },
    {
      headerName: 'Evaluation Date',
      field: 'evaluatedAt',
      sortable: true,

      filter: 'agDateColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'greaterThan', 'inRange'],
      },

      valueGetter: (params) => {
        return params?.data?.evaluatedAt
          ? moment(params?.data?.evaluatedAt).format('DD MMM YYYY')
          : '-';
      },
      cellRenderer: function EvaluationDateGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {params?.data?.evaluatedAt
              ? moment(params?.data?.evaluatedAt).format('DD MMM YYYY')
              : '-'}
          </div>
        );
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
        valueA - valueB,
    },

    {
      headerName: 'Project',
      field: 'project',
      sortable: false,

      filter: 'agSetColumnFilter',
      filterParams: {
        values: allProjectList?.map((project) => project.name),
      },
      width: 200,
      valueGetter: (params) => {
        return params?.data?.project?.attributes?.name;
      },
      cellRenderer: function (params) {
        return (
          <div>
            <SubmittedSolutionModal params={params} />
          </div>
        );
      },
    },

    {
      headerName: 'Category',
      field: 'category',
      sortable: false,

      filter: 'agSetColumnFilter',
      filterParams: {
        values: projectCategoryList?.map((category) => category.name),
      },

      width: 200,
      valueGetter: (params) => {
        return params?.data?.project?.attributes?.project_category?.data
          ?.attributes?.name;
      },
      cellRenderer: function CategoryGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {
              params?.data?.project?.attributes?.project_category?.data
                ?.attributes?.name
            }
          </div>
        );
      },
    },

    {
      headerName: 'Admin Status',
      field: 'adminStatus',
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['pending', 'on_hold', 'accepted'],
      },
      width: 250,

      cellRenderer: function StatusGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            <StatusDropdown params={params} />
          </div>
        );
      },
    },

    {
      headerName: 'On hold reason',
      field: 'onHoldReason',
      sortable: false,
      filter: false,
      width: 250,

      cellRenderer: function onHoldReason(params) {
        let text = '';
        if (
          params?.data?.adminFeedback &&
          params?.data?.adminFeedback?.length > 0
        )
          for (let i = 0; i < params?.data?.adminFeedback?.length; i++) {
            text = text + params?.data?.adminFeedback[i] + ',';
          }
        return (
          <div className='flex items-center h-full'>
            <OnHoldPopup params={params} />
          </div>
        );
      },
    },

    {
      field: 'createdAt',
      hide: true,
    },

    {
      field: 'previouslyEvaluatedAt',
      hide: true,
    },
    {
      field: 'projectId',
      hide: true,
    },
    {
      field: 'userId',
      hide: true,
    },

    {
      headerName: 'Resubmits',
      field: 'resubmits',
      sortable: false,

      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Resubmitted', 'Not Resubmitted'],
      },

      width: 250,

      cellRenderer: function StatusGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            {params?.data?.reSubmitted ? 'Resubmitted' : '-'}
          </div>
        );
      },
    },

    {
      headerName: 'Evaluator',
      field: 'evaluator',
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: [
          'contains',
          {
            displayKey: 'noEvaluator',
            displayName: 'No Evaluator',
            predicate: (_, cellValue) => !cellValue,
            numberOfInputs: 0,
          },
        ],
        suppressAndOrCondition: true,
      },

      width: 200,

      cellRenderer: function EvaluatorGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            <EvaluatorDropdown params={params} isFirstEvaluator={true} />
          </div>
        );
      },
    },

    {
      headerName: 'Evaluator Email',
      field: 'evaluatorEmail',
      sortable: false,

      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        suppressAndOrCondition: true,
      },

      hide: true,
      width: 220,
      valueGetter: (params) => {
        return params?.data?.Evaluators?.email;
      },
      cellRenderer: function (params) {
        return (
          <div className='flex items-center h-full '>
            {params.data?.Evaluators?.email}
          </div>
        );
      },
    },

    {
      headerName: 'Evaluation Status',
      field: 'evaluationStatus',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['pending', 'completed', 'rejected'],
      },
      sortable: false,
      width: 200,
      valueGetter: (params) => {
        return params?.data?.evaluationStatus || '-';
      },
      cellRenderer: function getEvaluationStatus(params) {
        return (
          <div className='flex items-center h-full p-6'>
            {params?.data?.evaluationStatus || '-'}
          </div>
        );
      },
    },

    {
      headerName: 'Score',
      field: 'score',
      sortable: false,
      filter: false,
      width: 200,
      valueFormatter: (params) => {
        return params?.data?.credits;
      },
      cellRenderer: function ScoreGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            <ScoreDetailsModal params={params} isFirstScore={true} />
          </div>
        );
      },
    },

    {
      headerName: 'Revaluation',
      field: 'revaluation',
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Requested', 'Not requested'],
      },
      width: 200,
      cellRenderer: function ScoreGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            {params?.data?.reEvaluationRequested ? 'Requested' : '-'}
          </div>
        );
      },
    },

    {
      headerName: 'Resubmit Evaluator',
      field: 'resubmitEvaluator',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        suppressAndOrCondition: true,
      },
      sortable: false,
      width: 200,
      cellRenderer: function ScoreGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            <EvaluatorDropdown params={params} isFirstEvaluator={false} />
          </div>
        );
      },
    },

    {
      headerName: 'Resubmit Evaluator Email',
      field: 'resubmitEvaluatorEmail',
      sortable: false,

      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        suppressAndOrCondition: true,
      },

      hide: true,
      width: 220,
      valueGetter: (params) => {
        return params?.data?.PreviousEvaluator?.email;
      },
      cellRenderer: function (params) {
        return (
          <div className='flex items-center h-full '>
            {params.data?.PreviousEvaluator?.email}
          </div>
        );
      },
    },

    {
      headerName: 'Re-score',
      field: 'rescore',
      sortable: false,
      filter: false,
      width: 200,
      cellRenderer: function ScoreGetter(params) {
        return (
          <div className='flex items-center h-full p-6'>
            <ScoreDetailsModal params={params} isFirstScore={false} />
          </div>
        );
      },
    },

    {
      headerName: 'Cohort',
      field: 'cohort',
      sortable: false,

      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        suppressAndOrCondition: true,
      },

      width: 200,
      valueGetter: (params) => {
        return params?.data?.UserSubscription?.cohortName;
      },
      cellRenderer: function CohortGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {params?.data?.UserSubscription?.cohortName}
          </div>
        );
      },
    },

    {
      headerName: 'Plan Type',
      field: 'plan_type',
      filter: false,
      sortable: false,
      width: 200,
      valueGetter: (params) => {
        return params?.data?.UserSubscription?.planType;
      },
      cellRenderer: function PlanGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {params?.data?.UserSubscription?.planType}
          </div>
        );
      },
    },
    {
      headerName: 'Accepted At',
      field: 'acceptedAt',
      width: 200,
      valueGetter: (params) => {
        return params?.data?.acceptedAt
          ? moment(params?.data?.acceptedAt).format('DD MMM YYYY')
          : '-';
      },
      cellRenderer: function AcceptedDateGetter(params) {
        return (
          <div className='flex items-center h-full'>
            {params?.data?.acceptedAt
              ? moment(params?.data?.acceptedAt).format('DD MMM YYYY')
              : '-'}
          </div>
        );
      },
    },
    {
      headerName: 'Accepted By',
      field: 'acceptedBy',
      width: 200,
      valueGetter: (params) => {
        return getAcceptedByName(params);
      },
      cellRenderer: (params) => {
        return (
          <div className='flex items-center h-full'>
            {getAcceptedByName(params)}
          </div>
        );
      },
    },
    {
      headerName: 'Actions',
      pinned: 'right',
      sortable: false,
      suppressMenu: true,
      width: 120,
      cellRenderer: ActionCellRenderer,
    },
  ];
};

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
};
